import React, { createContext, useEffect, useState } from "react";
import {
  AFFILIATE_SUBSCRIPTION,
  CHAT_HISTORY_DETAILS,
  CMS_BASE_URL,
  CONTACT_US,
  LOGIN,
  ME,
  NOTIFICATION,
  SAVE_TOKEN,
  SIGN_UP,
  USER_INVOICE_LIST,
  USER_SUBSCRIPTION_LIST,
} from "../utils/APIEndPoints";
import { CallAPI } from "../utils/apiActions";
import { useNavigate } from "react-router-dom";
import { showToast } from "../helper/toastHelper";
import { currency_rate, LABEL } from "../utils/constant";
import useAuth from "../hooks/useAuth";
import ExportCSV from "../utils/ExportCSV";
import { useCallAPI } from "../hooks/useCallAPI";

/**
 * Initial state for the dashboard context.
 * @type {Object}
 * @property {Array} planList - List of available plans.
 * @property {Object} currencyRate - Exchange rates for currencies.
 * @property {Array} activePlan - The user's current active plan.
 * @property {Array} allSubscribers - List of all subscribers (for admin/agency).
 * @property {Array} paymentHistory - User's payment history.
 * @property {Array} invoiceList - List of invoices.
 * @property {Array} allCMS - List of CMS data.
 * @property {Object|null} planPurchase - Details of the plan being purchased.
 * @property {boolean} isLoading - Global loading state.
 * @property {Object} dashbordAndAnalyticsData - Data for dashboard analytics.
 * @property {Object} chatDashbordAndAnalyticsData - Data for chat history analytics.
 * @property {Object} allNotification - User notifications.
 * @property {Array} invoiceListOfComission - List of commission invoices.
 * @property {boolean} isCSVLoading - Loading state for CSV export.
 */
const coolBizDasboardState = {
  planList: [],
  currencyRate: {},
  activePlan: [],
  allSubscribers: [],
  paymentHistory: [],
  invoiceList: [],
  allCMS: [],
  planPurchase: null,
  isLoading: false,
  dashbordAndAnalyticsData: {},
  chatDashbordAndAnalyticsData: {},
  allNotification: { notifications: [] },
  invoiceListOfComission: [],
  isCSVLoading: false,
};

const DashboardContext = createContext({
  ...coolBizDasboardState,
});

/**
 * Provider component for the Dashboard Context.
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Child components.
 * @returns {JSX.Element} The DashboardContext Provider.
 */
export const DasboardProvider = ({ children }) => {
  const navigate = useNavigate();
  // const { user, accessType } = useAuth();
  const user = {};
  const accessType = undefined;
  //"subscription/user-plan-list"
  const {
    data: allPlanRes,
    revalidate: fetchAllPlan,
    loading: isAllPlansLoading,
  } = useCallAPI({
    end_point: "SUBSCRIPTION_IN",
    method: "POST",
    apiUrl:
      user?.userType == 3
        ? `${CMS_BASE_URL.DASHBOARD.GET_ALL}?userType=${user.userType}`
        : `${CMS_BASE_URL.DASHBOARD.GET_ALL}`,
    enabled: false, // manual trigger
  });
  // subscription/user-active-subscription
  const {
    loading: isActivePlanLoading,
    data: activePlanRes,
    revalidate: fetchUserActivePlan,
  } = useCallAPI({
    end_point: "SUBSCRIPTION_IN", // placeholder, overridden at call time
    method: "POST",
    apiUrl: CMS_BASE_URL.DASHBOARD.ACTIVE_PLAN,
    enabled: false, // manual trigger
  });

  /* -------- DASHBOARD ANALYTICS -------- */
  const {
    data: dashboardAnalyticsRes,
    revalidate: fetchDashboardAnalytics,
    loading: dashboardAnalyticsResLoading,
  } = useCallAPI({
    end_point: "USER",
    method: "GET",
    apiUrl: CMS_BASE_URL.DASHBOARD.DASHBOARD_ANALYTICS,
    enabled: false,
  });

  /* -------- CHAT HISTORY ANALYTICS -------- */
  const {
    data: chatAnalyticsRes,
    revalidate: fetchChatAnalytics,
    loading: chatAnalyticsResLoading,
  } = useCallAPI({
    end_point: "CHAT",
    method: "GET",
    apiUrl: CHAT_HISTORY_DETAILS,
    enabled: false,
  });

  const {
    data: cmsRes,
    loading: cmsLoading,
    revalidate: fetchCmsByType,
  } = useCallAPI({
    end_point: "USER",
    method: "POST",
    apiUrl: "",
    body: {},
    enabled: false,
  });

  const [state, dispatch] = useState(coolBizDasboardState);

  /**
   * Updates a specific key in the dashboard state.
   * @param {string} key - The state key to update.
   * @param {*} value - The new value for the key.
   */
  const manageDasboardState = (key, value) =>
    dispatch((prev) => ({ ...prev, [key]: value }));

  /**
   * Fetches all available plans.
   * @param {string|null} [parentAgency=null] - The parent agency ID if applicable.
   * @param {string} [country="India"] - The country to filter plans by.
   */
  const getAllPlan = (parentAgency = null, country = "India") => {
    // console.log(user, "DasboardProvider");

    const servicePath =
      user?.userType == 3
        ? `${CMS_BASE_URL.DASHBOARD.GET_ALL}?userType=${user.userType}`
        : `${CMS_BASE_URL.DASHBOARD.GET_ALL}`;

    fetchAllPlan(
      country === "India" ? { country: "India" } : { country: "Other" },
      servicePath,
    );
  };

  useEffect(() => {
    if (!allPlanRes) return;

    if (allPlanRes?.res?.status === 200) {
      manageDasboardState(
        "planList",
        allPlanRes?.res?.data?.data?.plan_list || [],
      );

      manageDasboardState(
        "currencyRate",
        transformArrayToObject(allPlanRes?.res?.data?.data?.currency_rate) ||
          {},
      );
    }
  }, [allPlanRes]);

  /**
   * Transforms an array of currency rates into an object.
   * @param {Array} data - Array of currency rate objects.
   * @returns {Object} Object mapping currency names to rates.
   */
  const transformArrayToObject = (data) => {
    return data.reduce((acc, item) => {
      acc[item.currency_name] = item.rate;
      return acc;
    }, {});
  };

  /**
   * Fetches the current user's active plan.
   * @param {Object} payload - Payload containing user details.
   */
  const getUserActivePlan = (payload) => {
    fetchUserActivePlan({ ...payload, country: undefined });
  };

  useEffect(() => {
    if (isAllPlansLoading) {
      manageDasboardState("isLoading", true);
    } else {
      manageDasboardState("isLoading", false);
    }
  }, [isAllPlansLoading]);
  useEffect(() => {
    if (isActivePlanLoading) {
      manageDasboardState("isLoading", true);
    } else {
      manageDasboardState("isLoading", false);
    }
  }, [isActivePlanLoading]);

  useEffect(() => {
    if (!activePlanRes) return;

    if (activePlanRes?.res?.status === 200) {
      manageDasboardState("activePlan", activePlanRes?.res?.data?.data || []);
    }
  }, [activePlanRes]);

  /**
   * Fetches active subscribed users (for admin/agency).
   * @param {number} [userType=2] - The type of user.
   * @param {*} _ - Unused parameter.
   * @param {number} [page=1] - Page number for pagination.
   * @param {string} [userId=""] - User ID filter.
   * @param {number|null} [isChild=null] - Filter for child accounts.
   * @param {string} [country="India"] - Country context.
   */
  const getUsersActivePlan = async (
    userType = 2,
    _,
    page = 1,
    userId = "",
    isChild = null,
    country = "India",
  ) => {
    manageDasboardState("isLoading", true);
    const servicePath =
      country === "India" && accessType != 1
        ? "SUBSCRIPTION_IN"
        : "SUBSCRIPTION_OTHERS";
    let url = `${CMS_BASE_URL.DASHBOARD.ALL_ACTIVE_SUBSCRIBED_USER}?page=${page}&userType=${userType}&parentId=${userId}`;
    if (isChild == 1) {
      url = `${url}&isChild=1`;
    }
    const response = await CallAPI(servicePath, "GET", url);
    manageDasboardState("isLoading", false);
    if (response?.res?.status === 200) {
      manageDasboardState("allSubscribers", response?.res?.data?.data || {});
    }
  };

  /**
   * Fetches payment history for a user.
   * @param {string} userId - The user ID.
   * @param {number} page - Page number.
   * @param {string} [country="India"] - Country context.
   */
  const getUsersPaymentHistory = async (userId, page, country = "India") => {
    manageDasboardState("isLoading", true);
    const servicePath =
      country === "India" ? "SUBSCRIPTION_IN" : "SUBSCRIPTION_OTHERS";
    const response = await CallAPI(
      servicePath,
      "GET",
      `${USER_SUBSCRIPTION_LIST}?userId=${userId}&page=${page}`,
    );
    manageDasboardState("isLoading", false);
    if (response?.res?.status === 200) {
      manageDasboardState("paymentHistory", response?.res?.data?.data || []);
    }
  };

  /**
   * Fetches the list of invoices.
   * @param {string} subscriptionId - The subscription ID or User ID.
   * @param {number} [page=1] - Page number.
   * @param {boolean} [viauserId=false] - Whether to fetch by User ID instead of Subscription ID.
   * @param {string} [country="India"] - Country context.
   */
  const getInvoiceList = async (
    subscriptionId,
    page = 1,
    viauserId = false,
    country = "India",
  ) => {
    manageDasboardState("isLoading", true);
    const servicePath =
      country === "India" ? "SUBSCRIPTION_IN" : "SUBSCRIPTION_OTHERS";
    const response = await CallAPI(
      servicePath,
      "GET",
      `${USER_INVOICE_LIST}?${
        viauserId ? "userId" : "subscriptionId"
      }=${subscriptionId}&page=${page}`,
    );
    manageDasboardState("isLoading", false);
    if (response?.res?.status === 200) {
      manageDasboardState("invoiceList", response?.res?.data?.data || []);
    }
  };

  /**
   * Downloads a specific invoice.
   * @param {Object} payload - Payload containing invoice details.
   * @returns {Promise<Object|null>} The invoice data or null.
   */
  const downloadInvoice = async (payload) => {
    // manageDasboardState('isLoading', true);
    const servicePath =
      payload.country === "inr" ? "SUBSCRIPTION_IN" : "SUBSCRIPTION_OTHERS";
    const response = await CallAPI(
      servicePath,
      "POST",
      CMS_BASE_URL.DASHBOARD.DOWNLOAD_INVOICE,
      { ...payload, country: undefined },
    );
    // manageDasboardState('isLoading', false);
    if (response?.status) {
      return response.res.data.data || null;
    }
  };

  /**
   * Cancels a user's subscription.
   * @param {Object} payload - Payload containing subscription ID and country.
   * @param {Function} cb - Callback function on success.
   */
  const cancelSubscription = async (payload, cb) => {
    manageDasboardState("isLoading", true);
    const servicePath =
      payload.country === "India" ? "SUBSCRIPTION_IN" : "SUBSCRIPTION_OTHERS";
    const response = await CallAPI(
      servicePath,
      "POST",
      CMS_BASE_URL.DASHBOARD.CANCEL_PLAN,
      { subscriptionId: payload.subscriptionId },
    );
    manageDasboardState("isLoading", false);
    if (response?.res?.status === 200) {
      cb(response.res.data.message);
    }
  };

  /**
   * Creates a "Contact Us" record.
   * @param {Object} payload - Form data.
   * @param {Function} cb - Callback function on success.
   * @param {string} type - Type of contact.
   */
  const contactUsRecordCreate = async (payload, cb, type) => {
    manageDasboardState("isLoading", true);
    const response = await CallAPI("USER", "POST", CONTACT_US, payload, type);
    manageDasboardState("isLoading", false);
    if (response?.res?.status === 200) {
      cb(response.res.data.message);
    }
  };

  /**
   * Fetches CMS data by type.
   * @param {number} [type=1] - CMS type ID.
   * @param {string|null} [parentAgency=null] - Parent agency ID.
   * @param {number} page - Page number.
   * @param {string} [order="DESC"] - Sort order.
   */
  const getCmsByCmsType = async (
    type = 1,
    parentAgency = null,
    page,
    order = "DESC",
  ) => {
    // manageDasboardState("isLoading", true);
    let url = !page
      ? `${CMS_BASE_URL.CMS.GET_ALL}${type}`
      : `${CMS_BASE_URL.CMS.GET_ALL}${type}?page=${page}&order=${order}`;
    let data = parentAgency ? { agencyId: parentAgency } : {};
    // const response = await CallAPI("USER", "POST", url, data);

    fetchCmsByType(data, url);
    // manageDasboardState("isLoading", false);
    // if (response?.res?.status === 200) {
    //   manageDasboardState("allCMS", response?.res?.data?.data || []);
    // }
  };

  useEffect(() => {
    manageDasboardState("isLoading", cmsLoading);
  }, [cmsLoading]);

  useEffect(() => {
    if (cmsRes?.res?.status === 200) {
      manageDasboardState("allCMS", cmsRes.res.data?.data || []);
    }
  }, [cmsRes]);

  /**
   * Fetches specific CMS documents (CIG, FAQ, OT).
   * @param {string|null} [parentAgency=null] - Parent agency ID.
   * @returns {Promise<Object|null>} Object containing categorized CMS data.
   */
  const getCmsDocument = async (parentAgency = null) => {
    manageDasboardState("isLoading", true);
    let data = parentAgency ? { agencyId: parentAgency } : {};
    let url1 = `${CMS_BASE_URL.CMS.GET_ALL}1`;
    let url2 = `${CMS_BASE_URL.CMS.GET_ALL}2`;
    let url3 = `${CMS_BASE_URL.CMS.GET_ALL}3`;
    try {
      const [type1Data, type2Data, type3Data] = await Promise.all([
        CallAPI("USER", "POST", url1, data),
        CallAPI("USER", "POST", url2, data),
        CallAPI("USER", "POST", url3, data),
      ]);

      manageDasboardState("isLoading", false);
      return {
        cig:
          type1Data?.res?.status === 200
            ? type1Data?.res?.data?.data?.cmsData
            : [],
        faq:
          type2Data?.res?.status === 200
            ? type2Data?.res?.data?.data?.cmsData
            : [],
        ot:
          type3Data?.res?.status === 200
            ? type3Data?.res?.data?.data?.cmsData
            : [],
      };
    } catch (error) {
      manageDasboardState("isLoading", false);
      console.error("Error fetching CMS documents:", error);
      return null;
    }
  };

  /**
   * Fetches affiliate-related CMS data.
   * @param {string|null} [parentAgency=null] - Parent agency ID.
   * @returns {Promise<Object|null>} Object containing affiliate CMS sections.
   */
  const getCmsAffiliate = async (parentAgency = null) => {
    manageDasboardState("isLoading", true);
    let data = parentAgency ? { agencyId: parentAgency } : {};
    let url1 = `${CMS_BASE_URL.CMS.GET_ALL}8`;
    let url2 = `${CMS_BASE_URL.CMS.GET_ALL}9`;
    let url3 = `${CMS_BASE_URL.CMS.GET_ALL}10`;
    let url4 = `${CMS_BASE_URL.CMS.GET_ALL}11`;
    let url5 = `${CMS_BASE_URL.CMS.GET_ALL}14`;
    try {
      const [type1Data, type2Data, type3Data, type4Data, type5Data] =
        await Promise.all([
          CallAPI("USER", "POST", url1, data),
          CallAPI("USER", "POST", url2, data),
          CallAPI("USER", "POST", url3, data),
          CallAPI("USER", "POST", url4, data),
          CallAPI("USER", "POST", url5, data),
        ]);

      manageDasboardState("isLoading", false);
      return {
        affliliateTitle:
          type1Data?.res?.status === 200
            ? type1Data?.res?.data?.data?.cmsData[0]
            : [],
        affliliateHeader:
          type2Data?.res?.status === 200
            ? type2Data?.res?.data?.data?.cmsData[0]
            : [],
        affliliateBenifit:
          type3Data?.res?.status === 200
            ? type3Data?.res?.data?.data?.cmsData
            : [],
        affliliateFooter:
          type4Data?.res?.status === 200
            ? type4Data?.res?.data?.data?.cmsData[0]
            : [],
        affiliateMarketing:
          type5Data?.res?.status === 200
            ? type5Data?.res?.data?.data?.cmsData[0]
            : [],
      };
    } catch (error) {
      manageDasboardState("isLoading", false);
      console.error("Error fetching CMS documents:", error);
      return null;
    }
  };

  /**
   * Checks if a promo code is valid.
   * @param {Object} payload - Payload containing promo code details.
   * @returns {Promise<Object>} Response data.
   */
  const checkPromocode = async (payload) => {
    manageDasboardState("isLoading", true);
    const response = await CallAPI(
      "SUBSCRIPTION_OTHERS",
      "POST",
      "subscription/check-promoCode",
      payload,
    );
    manageDasboardState("isLoading", false);
    if (response?.res?.status === 200) {
      return response.res.data;
    }
  };

  /**
   * Fetches dashboard and analytics data.
   * @param {string} [dateRange=""] - Date range filter (e.g., year).
   * @param {Object} userDetails - User details for filtering.
   */
  const getDashboardAndAnalyticsData = (dateRange = "", userDetails) => {
    const buildUrlWithDateRange = (baseUrl, dateRange) => {
      let url = baseUrl;

      if (dateRange && baseUrl === CHAT_HISTORY_DETAILS) {
        url = `${baseUrl}?startYear=${dateRange}&parentId=${userDetails?.parentId}&parentType=${userDetails?.parentType}&userType=${userDetails?.userType}&userUuid=${userDetails?.userUuid}`;
      }

      if (dateRange && baseUrl === CMS_BASE_URL.DASHBOARD.DASHBOARD_ANALYTICS) {
        url = `${baseUrl}?year=${dateRange}`;
      }

      return url;
    };

    const analyticsUrl = buildUrlWithDateRange(
      CMS_BASE_URL.DASHBOARD.DASHBOARD_ANALYTICS,
      dateRange,
    );

    fetchDashboardAnalytics(undefined, analyticsUrl);

    if (dateRange) {
      const chatUrl = buildUrlWithDateRange(CHAT_HISTORY_DETAILS, dateRange);
      fetchChatAnalytics(undefined, chatUrl);
    }
  };

  useEffect(() => {
    if (dashboardAnalyticsRes?.res?.status !== 200) return;

    manageDasboardState(
      "dashbordAndAnalyticsData",
      dashboardAnalyticsRes.res.data?.data || {},
    );
  }, [dashboardAnalyticsRes]);

  useEffect(() => {
    if (chatAnalyticsRes?.res?.status !== 200) return;

    manageDasboardState(
      "chatDashbordAndAnalyticsData",
      chatAnalyticsRes.res.data || {},
    );
  }, [chatAnalyticsRes]);

  useEffect(() => {
    if (dashboardAnalyticsResLoading) {
      manageDasboardState("isLoading", true);
    } else {
      manageDasboardState("isLoading", false);
    }
  }, [dashboardAnalyticsResLoading]);
  useEffect(() => {
    if (chatAnalyticsResLoading) {
      manageDasboardState("isLoading", true);
    } else {
      manageDasboardState("isLoading", false);
    }
  }, [chatAnalyticsResLoading]);

  /**
   * Sets the plan purchase state.
   * @param {Object} value - Plan purchase details.
   */
  const setPlanPurchase = async (value) => {
    manageDasboardState("planPurchase", value);
  };
  /**
   * Saves the FCM token for push notifications.
   * @param {Object} payload - Payload containing the token.
   */
  const saveFcmToken = async (payload) => {
    const response = await CallAPI(
      "USER",
      "POST",
      SAVE_TOKEN,
      payload,
      "",
      false,
    );
    if (response?.res?.status === 200) {
      console.warn("saved");
    }
  };
  /**
   * Fetches user notifications.
   * @param {number} [page=1] - Page number.
   */
  const getNotification = async (page = 1) => {
    manageDasboardState("isLoading", true);
    const response = await CallAPI(
      "USER",
      "GET",
      `${NOTIFICATION}?page=${page}`,
    );
    manageDasboardState("isLoading", false);
    if (response?.res?.status === 200) {
      manageDasboardState(
        "allNotification",
        response?.res?.data?.data?.response || { notifications: [] },
      );
    }
  };
  /**
   * Fetches the list of commission invoices.
   * @param {string} subscriptionId - User ID (used as subscription ID context here).
   * @param {number} [page=1] - Page number.
   * @param {string} [country="India"] - Country context.
   */
  const getInvoiceListOfComission = async (
    subscriptionId,
    page = 1,
    country = "India",
  ) => {
    manageDasboardState("isLoading", true);
    const servicePath =
      country === "India" ? "SUBSCRIPTION_IN" : "SUBSCRIPTION_OTHERS";
    const response = await CallAPI(
      servicePath,
      "GET",
      `${AFFILIATE_SUBSCRIPTION.TRANSFER_LIST}?userId=${subscriptionId}&page=${page}`,
    );
    manageDasboardState("isLoading", false);
    if (response?.res?.status === 200) {
      manageDasboardState(
        "invoiceListOfComission",
        response?.res?.data?.data || [],
      );
    }
  };
  /**
   * Exports subscription data as a CSV file.
   * @param {number} [userType=2] - User type.
   * @param {*} _ - Unused parameter.
   * @param {string} [userId=""] - User ID filter.
   * @param {number|null} [isChild=null] - Child account filter.
   */
  const handleExportSubscriptionCSV = async (
    userType = 2,
    _,
    userId = "",
    isChild = null,
  ) => {
    manageDasboardState("isCSVLoading", true);
    let url = `${CMS_BASE_URL.DASHBOARD.SUBSCRIPTION_EXPORT}?sortBy=DESC&userType=${userType}&parentId=${userId}`;
    if (isChild == 1) {
      url = `${url}&isChild=1`;
    }
    const response = await CallAPI("SUBSCRIPTION_OTHERS", "GET", url);
    manageDasboardState("isCSVLoading", false);
    console.log("resPPPPPPP", response);
    if (response.res.status) {
      // const fileName = response?.res?.headers?.get('Content-Disposition')||""
      ExportCSV(
        response.res.data,
        `Content-Disposition: attachment; filename="Subcription-report.csv"`,
      );
    }
  };

  const coolBizDasboardValues = {
    ...state,
    getAllPlan,
    setPlanPurchase,
    getUsersActivePlan,
    cancelSubscription,
    getUserActivePlan,
    getUsersPaymentHistory,
    getInvoiceList,
    downloadInvoice,
    contactUsRecordCreate,
    getCmsByCmsType,
    getCmsDocument,
    getCmsAffiliate,
    checkPromocode,
    getDashboardAndAnalyticsData,
    saveFcmToken,
    getNotification,
    getInvoiceListOfComission,
    handleExportSubscriptionCSV,
    // downloadInvoiceOfComission
  };

  return (
    <DashboardContext.Provider value={coolBizDasboardValues}>
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardContext;

/**
 * Custom hook to fetch plans.
 * @param {string} [country="India"] - Country context.
 * @param {string|null} [parentAgency=null] - Parent agency ID.
 * @param {number|null} [userType=null] - User type.
 * @returns {Object} API response object.
 */
export function usePlans(
  country = "India",
  parentAgency = null,
  userType = null,
) {
  const apiUrl =
    country === "India" ? "SUBSCRIPTION_IN" : "SUBSCRIPTION_OTHERS";
  let url = CMS_BASE_URL.DASHBOARD.GET_ALL;
  if (userType === 3) {
    url = `${url}?userType=${userType}`;
  }
  if (parentAgency) {
    url = `${url}?agency=${parentAgency}`;
  }

  return useCallAPI({
    end_point: url,
    method: "POST",
    apiUrl,
    body: { country },
    ttl: 30_000,
  });
}

/**
 * Custom hook to fetch user active plan.
 * @param {Object} payload - Payload containing user details.
 * @returns {Object} API response object.
 */
export function useUserActivePlan(payload) {
  const apiUrl =
    payload.country === "India" ? "SUBSCRIPTION_IN" : "SUBSCRIPTION_OTHERS";
  return useCallAPI({
    end_point: CMS_BASE_URL.DASHBOARD.ACTIVE_PLAN,
    method: "POST",
    apiUrl,
    body: { ...payload, country: undefined },
    ttl: 30_000,
  });
}

/**
 * Custom hook to fetch payment history.
 * @param {string} userId - User ID.
 * @param {number} [page=1] - Page number.
 * @param {string} [country="India"] - Country context.
 * @returns {Object} API response object.
 */
export function usePaymentHistory(userId, page = 1, country = "India") {
  const apiUrl =
    country === "India" ? "SUBSCRIPTION_IN" : "SUBSCRIPTION_OTHERS";
  return useCallAPI({
    end_point: `${USER_SUBSCRIPTION_LIST}?userId=${userId}&page=${page}`,
    method: "GET",
    apiUrl,
    ttl: 30_000,
  });
}

/**
 * Custom hook to fetch invoice list.
 * @param {string} subscriptionId - Subscription ID.
 * @param {number} [page=1] - Page number.
 * @param {boolean} [viaUserId=false] - Whether to use User ID.
 * @param {string} [country="India"] - Country context.
 * @returns {Object} API response object.
 */
export function useInvoiceList(
  subscriptionId,
  page = 1,
  viaUserId = false,
  country = "India",
) {
  const apiUrl =
    country === "India" ? "SUBSCRIPTION_IN" : "SUBSCRIPTION_OTHERS";
  const end_point = `${USER_INVOICE_LIST}?${viaUserId ? "userId" : "subscriptionId"}=${subscriptionId}&page=${page}`;
  return useCallAPI({
    end_point,
    method: "GET",
    apiUrl,
    ttl: 30_000,
  });
}
